import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '../lib/cn.js'
import { useConnectionState } from '../lib/realtime.js'
import { useSession } from '../lib/session.js'
import { errorMessage, setBearerToken, useTRPC } from '../lib/trpc.js'
import { FieldShell } from './field/FieldShell.js'
import { useAppVersion } from '../lib/app-version.js'
import { GuideProvider, useGuide } from '../guide/GuideProvider.js'
import { Icon } from './Icon.js'
import { useDismiss } from './overlay.js'
import { JobOverlay } from './JobOverlay.js'
import { Eco, EcoCornerButton, EcoRailButton } from './eco/Eco.js'
import { useIsField } from '../lib/media.js'
import { NAV, activeNavItem, useUnreadCount, type NavItem } from './nav.js'
import { Avatar } from './ui.js'

/**
 * The application frame.
 *
 * One component serves both surfaces rather than two codebases: a 72px icon
 * rail on a wide screen, a bottom bar on a narrow one. The bottom bar is
 * where the thumb is, and it carries only the four things a technician needs in
 * the field — everything else lives behind "More", because a nine-item bottom
 * bar is unusable at arm's length in a van.
 *
 * The rail is light and narrow on purpose. The board lives on an ink-100 page
 * of white cards, and a dark strip beside it was a second colour system for
 * nine icons that never change. Ink-100 matches the page; the active item is
 * ink-900, not green. The 9px label is a reminder rather than a signpost, so
 * every item keeps a real accessible name and the mobile bar keeps its full
 * text.
 *
 * Settings is a rail item as well as the avatar at the foot of it, because an
 * account-wide screen that can only be reached by pressing your own face is a
 * screen people report as missing. The avatar stays: it is the only element
 * always on screen, so it carries the connection state.
 *
 * People is a Settings section, not a tenth rail item. Decision 65. A person
 * page still lights Settings, because that is where you came from.
 *
 * **Tasks is the tenth, and only for the office.** Decision 38 said nine fit
 * and to revisit when a tenth screen wanted a place; this one does, and the
 * `TASK_MANAGE` gate means a technician's rail stays at eight while the
 * office's goes to ten — which still does not scroll at any desktop height the
 * board is usable at. A phone never sees this rail: it is the field app
 * (decision 93), whose screens other than its tabs are lists under Me.
 */

export function AppShell() {
  const { can } = useSession()
  const field = useIsField()
  const visible = NAV.filter((item) => !item.permission || can(item.permission))

  // A phone, and any touch-first screen at any width, is the field app: the
  // technician's tabs and a job that slides up. Decision 93. Both get the
  // guide: help, the welcome tour and What's new. Decision 115.
  if (field) {
    return (
      <GuideProvider>
        <FieldShell />
        <EcoCornerButton />
        <Eco />
      </GuideProvider>
    )
  }

  return (
    <GuideProvider>
      <div className="flex h-full bg-ink-100">
        <DesktopRail items={visible} />

        <div className="flex min-w-0 flex-1 flex-col">
          <main className="min-h-0 flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>

        {/* Every screen can open a job, so the popup belongs to the shell rather
            than to any of them. Decision 61. */}
        <JobOverlay />
        {/* Eco, the assistant, beside whatever is open. Decision 116. */}
        <Eco />
      </div>
    </GuideProvider>
  )
}

function DesktopRail({ items }: { items: NavItem[] }) {
  const { user } = useSession()
  const connection = useConnectionState()
  const location = useLocation()
  const current = activeNavItem(items, location.pathname)

  const offline =
    connection === 'connecting' ? 'Reconnecting…' : connection === 'open' ? null : 'Offline — changes may be stale'

  return (
    <aside
      data-guide="rail"
      className="hidden w-[var(--sidebar-width)] shrink-0 flex-col items-center gap-1 bg-ink-900 py-2.5 lg:flex"
    >
      <img
        src="/brand/ecopest-mark-colour-on-dark.svg"
        alt="EcoPest"
        className="mt-0.5 mb-2 h-7 w-auto"
      />

      <nav className="flex flex-1 flex-col items-center gap-1">
        {items.map((item) => {
          const active = item === current
          return (
            <Link
              key={item.to}
              to={item.to}
              title={item.label}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'relative flex w-14 flex-col items-center gap-[3px] rounded-md py-1.5 transition-colors',
                active
                  ? 'bg-white/[0.09] font-semibold text-white shadow-[inset_3px_0_0_var(--color-brand-500)]'
                  : 'text-ink-400 hover:bg-white/[0.06] hover:text-white',
              )}
            >
              {/* The rail is the Charcoal frame the command bar joins: the
                  current page is lit, with an Eco Green bar and glyph.
                  Decisions 102 and 105. */}
              <Icon name={item.icon} className={active ? 'text-brand-500' : undefined} />
              <span className="text-[9px] font-semibold">{item.label}</span>
              {item.to === '/inbox' ? <UnreadDot className="top-1 right-2.5" /> : null}
            </Link>
          )
        })}
      </nav>

      <EcoRailButton />
      <HelpButton />
      <AccountMenu offline={offline} />
    </aside>
  )
}

/**
 * Help, above the avatar: the panel of guides for the screen you are on. The
 * same as pressing `?`, for the people who never will. A green square says a
 * release note is waiting, as the Inbox's says mail is. Decision 115.
 */
function HelpButton() {
  const guide = useGuide()
  const { pathname } = useLocation()
  const waiting = guide.unseen.length > 0
  // A bug report with a fix to check or a question is the reporter's to answer. Decision 117.
  const bugTurn = guide.bugs.waitingOnYou > 0
  // Lit while the panel is open, and on the help centre, which has no rail item of its own.
  const on = guide.helpOpen || pathname.startsWith('/help')
  return (
    <button
      type="button"
      data-guide="help-button"
      onClick={guide.toggleHelp}
      aria-expanded={guide.helpOpen}
      aria-label={bugTurn ? 'Help — a bug report is waiting on you' : waiting ? 'Help — new changes to read' : 'Help'}
      aria-keyshortcuts="?"
      title="Help (?)"
      className={cn(
        'relative mt-auto mb-1.5 flex w-14 flex-col items-center gap-[3px] rounded-md py-1.5 transition-colors',
        on
          ? 'bg-white/[0.09] text-white shadow-[inset_3px_0_0_var(--color-brand-500)]'
          : 'text-ink-400 hover:bg-white/[0.06] hover:text-white',
      )}
    >
      <Icon name="help" className={on ? 'text-brand-500' : undefined} />
      <span className="text-[9px] font-semibold">Help</span>
      {waiting || bugTurn ? (
        <span aria-hidden="true" className="absolute top-1 right-2.5 size-2 rounded-[2px] bg-brand-500" />
      ) : null}
    </button>
  )
}

/**
 * The avatar opens a short menu rather than navigating, so signing out is two
 * clicks from any screen instead of a trip to Settings → Account. My profile
 * is only offered to somebody who can open person pages — `users.get` is
 * `USER_MANAGE`, and a link to a page that refuses you is worse than none.
 *
 * The wide sidebar had room to write the connection state out. Here it is a
 * red ring on the avatar, which is the only element that is always on
 * screen, with the sentence in the tooltip and at the top of the menu.
 */
function AccountMenu({ offline }: { offline: string | null }) {
  const { user, can } = useSession()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useDismiss(root, open, () => setOpen(false))

  const logout = useMutation(
    trpc.auth.logout.mutationOptions({
      onSuccess: () => {
        setBearerToken(null)
        queryClient.clear()
        navigate('/login', { replace: true })
      },
    }),
  )

  const name = user?.name ?? 'Account'
  const guide = useGuide()
  const unseen = guide.unseen.reduce((sum, release) => sum + release.items.length, 0)
  const version = useAppVersion()
  const versionLine = version.current ?? version.behind
  const itemClass =
    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink-700 hover:bg-ink-50 hover:text-ink-900'

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-label="Account"
        aria-haspopup="menu"
        aria-expanded={open}
        title={offline ? `${name} · ${offline}` : name}
        onClick={() => setOpen((was) => !was)}
        className="block rounded-md"
      >
        <Avatar
          name={user?.name ?? '?'}
          colour={user?.colour}
          className={cn('size-7', offline && 'ring-2 ring-red-600')}
        />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Account"
          className="absolute bottom-0 left-full z-50 ml-3 w-60 overflow-hidden rounded-lg bg-white py-1 shadow-[0_16px_40px_rgb(15_23_42_/_0.22),0_4px_12px_rgb(15_23_42_/_0.1)] ring-1 ring-ink-200"
        >
          <div className="border-b border-ink-100 px-3 pt-2 pb-2.5">
            <p className="truncate text-sm font-semibold text-ink-900">{name}</p>
            {user?.email ? <p className="truncate text-xs text-ink-500">{user.email}</p> : null}
            {offline ? <p className="mt-1 text-xs text-red-700">{offline}</p> : null}
            {versionLine ? <p className="mt-1 text-xs text-ink-400">{versionLine}</p> : null}
          </div>

          <Link
            role="menuitem"
            to="/settings?tab=account"
            onClick={() => setOpen(false)}
            className={itemClass}
          >
            <Icon name="settings" className="size-4" />
            Account settings
          </Link>
          {user && can('USER_MANAGE') ? (
            <Link
              role="menuitem"
              to={`/people/${user.id}`}
              onClick={() => setOpen(false)}
              className={itemClass}
            >
              <Icon name="clients" className="size-4" />
              My profile
            </Link>
          ) : null}

          <div className="my-1 border-t border-ink-100" />

          <Link role="menuitem" to="/help" onClick={() => setOpen(false)} className={itemClass}>
            <Icon name="book" className="size-4" />
            Help centre
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              guide.openWhatsNew()
            }}
            className={itemClass}
          >
            <Icon name="news" className="size-4" />
            <span className="flex-1">What’s new</span>
            {unseen > 0 ? (
              <span className="rounded-sm bg-ink-900 px-1.5 py-px text-[10px] font-bold text-white tabular-nums">
                {unseen}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              guide.startTour()
            }}
            className={itemClass}
          >
            <Icon name="flag" className="size-4" />
            Take the tour
          </button>

          <div className="my-1 border-t border-ink-100" />

          <button
            type="button"
            role="menuitem"
            disabled={logout.isPending}
            onClick={() => logout.mutate()}
            className={cn(itemClass, 'disabled:opacity-60')}
          >
            <Icon name="signOut" className="size-4" />
            {logout.isPending ? 'Signing out…' : 'Sign out'}
          </button>
          {logout.error ? (
            <p className="px-3 pb-2 text-xs text-red-600">{errorMessage(logout.error)}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Unread mail is a dot, not a number, on both surfaces. The rail has no room
 * for a count and the bottom bar has no room to spare; either way what a
 * person does about it is the same.
 */
function UnreadDot({ className }: { className?: string }) {
  const count = useUnreadCount()
  if (!count) return null

  return (
    <span
      aria-label={`${count} unread`}
      className={cn('absolute top-2 right-1/2 ml-4 size-2 rounded-[2px] bg-brand-600', className)}
    />
  )
}
